import { ref, computed, onMounted } from 'vue'
import { scrollToFirstError } from '../../foundation/js/inputValidators/scrollToFirstError'
import { useStore } from 'vuex'
import emailValidator from '../../foundation/js/inputValidators/emailValidator'
import phoneValidator from '../../foundation/js/inputValidators/phoneValidator'
import nameValidator from '../../foundation/js/inputValidators/nameValidator'
import cprValidator from '../../foundation/js/inputValidators/cprValidator'
import textAreaValidator from '../../foundation/js/inputValidators/textAreaValidator'
import stripHtml from 'foundation/js/stripHtml'
import FormInputTextField from 'components/FormInputTextField/FormInputTextField.vue'
import TooltipComponent from 'components/TooltipComponent/TooltipComponent.vue'
import FormAddressField from 'components/FormAddressField/FormAddressField.vue'
import FormInputRichText from 'components/FormInputRichText/FormInputRichText.vue'
import DonationAmountPicker from 'components/DonationAmountPicker/DonationAmountPicker.vue'

export default {
    name: 'FundraiseForm',
    components: {
        FormInputTextField,
        FormInputRichText,
        TooltipComponent,
        FormAddressField,
        DonationAmountPicker
    },
    props: {
        fundraisingCampaignId: { type: String, required: true },
        cancelUrl: { type: String, required: false },
        isMindegave: { type: Boolean, required: false },
        draft: { type: Object, required: false },
        showUnsavedChangesWarning: { type: Boolean, required: false },
        showPersonalInformationInput: { type: Boolean, required: false },
        firstName: { type: String, required: false },
        lastName: { type: String, required: false },
        email: { type: String, required: false },
        phone: { type: String, required: false }
    },
    setup(props) {
        const store = useStore()
        const donationSettings = computed(() => store.state.fundraising.donation_settings)
        const pageId = computed(() => store.state.main.page_id)
        const getLabel = (labelKey) => store.getters['labels/getLabel']({ labelCollection: 'fundraiseForm', labelKey })
        const isSubmitting = computed(() => store.state.fundraising.isSubmitting)
        const showPersonalInformationInputValue = computed(() => props.showPersonalInformationInput)
        const showUnsavedChangesWarning = props.showUnsavedChangesWarning

        const handleBeforeUnload = (e) => {
            if (store.state.fundraising.isEditing && showUnsavedChangesWarning) {
                e.preventDefault()
                e.returnValue = ''
            }
        }
        if (showUnsavedChangesWarning) {
            window.addEventListener('beforeunload', handleBeforeUnload)
        } else {
            window.removeEventListener('beforeunload', handleBeforeUnload)
        }

        const unSubmitted = ref(true)

        const firstNameInput = ref(props.draft?.contactFirstName ?? '')
        const lastNameInput = ref(props.draft?.contactLastName ?? '')
        const phoneInput = ref(props.draft?.mobilePhone ?? '')
        const emailInput = ref(props.draft?.email ?? '')

        const firstNameInputFilled = computed(() => {
            if (props.firstName) return nameValidator(props.firstName)
            return unSubmitted.value && !firstNameInput.value ? true : nameValidator(firstNameInput.value)
        })
        const lastNameInputFilled = computed(() => {
            if (props.lastName) return nameValidator(props.lastName)
            return unSubmitted.value && !lastNameInput.value ? true : nameValidator(lastNameInput.value)
        })
        const phoneInputFilled = computed(() => {
            if (props.phone) return phoneValidator(props.phone)
            return unSubmitted.value ? true : phoneValidator(phoneInput.value)
        })
        const emailInputFilled = computed(() => {
            if (props.email) return emailValidator(props.email)
            return unSubmitted.value ? true : emailValidator(emailInput.value)
        })

        const cprNumberInput = ref(props.draft?.cprNo ?? '')
        const cprNumberInputFilled = computed(() => unSubmitted.value ? true : cprValidator(cprNumberInput.value))

        const cvrNumberInput = ref(props.draft?.cvrNo ?? '')
        const cvrNumberInputFilled = computed(() => unSubmitted.value ? true : cvrNumberInput.value.length > 2)

        const addressLineInput = ref(props.draft?.addressLine1 ?? '')
        const addressLineInputFilled = computed(() => unSubmitted.value ? true : addressLineInput.value.length > 2 && textAreaValidator(addressLineInput.value))
        const postalCodeInput = ref(props.draft?.postalCode ?? '')
        const postalCodeInputFilled = computed(() => unSubmitted.value ? true : postalCodeInput.value.length === 4 && textAreaValidator(addressLineInput.value))
        const cityInput = ref(props.draft?.city ?? '')
        const cityInputFilled = computed(() => unSubmitted.value ? true : cityInput.value.length > 0 && textAreaValidator(addressLineInput.value))

        const paymentMethodInput = ref(props.draft?.paymentMethod ?? '')
        const paymentMethodInputFilled = computed(() => unSubmitted.value ? true : !!paymentMethodInput.value)

        const anonymousDonationInput = ref(props.draft?.isAnonymous ? '1' : '0')
        const isAnonymous = computed(() => anonymousDonationInput.value === '1')

        const customerTypeInput = ref(props.draft?.customerType == 0 ? 'Company' : 'Private')
        const isPrivate = computed(() => customerTypeInput.value === 'Private')
        const isCompany = computed(() => customerTypeInput.value === 'Company')
        const companyNameInput = ref(props.draft?.companyName ?? '')
        const companyNameInputFilled = computed(() => unSubmitted.value && !companyNameInput.value ? true : textAreaValidator(companyNameInput.value))

        const commentsInput = ref(props.draft?.comments ?? '')
        const commentsInputFilled = computed(() => {
            const text = stripHtml(commentsInput.value)
            return unSubmitted.value && !commentsInput.value ? true : commentsInput.value ? !!text : true
        })

        const taxDeductibleInput = ref(props.draft?.isTaxDeductible ?? false)

        const donationAmountInput = ref('')
        const donationAmountInputFilled = computed(() => unSubmitted.value ? true : donationAmountInput.value)

        const setDonationAmountFromSelector = (v) => {
            donationAmountInput.value = v ?? ''
        }

        const addressIsRequired = computed(() => isCompany.value || props.isMindegave)
        const cprIsRequired = computed(() => isPrivate.value && taxDeductibleInput.value)
        const cvrIsRequired = computed(() => isCompany.value)

        const inputIsValid = function () {
            const addressFilled = addressLineInputFilled.value && postalCodeInputFilled.value && cityInputFilled.value
            const donationAmountValid = !!donationAmountInput.value

            const companyNameValid = isPrivate.value || companyNameInputFilled.value
            const firstNameValid = props.firstName ? nameValidator(props.firstName) : firstNameInputFilled.value
            const lastNameValid = props.lastName ? nameValidator(props.lastName) : lastNameInputFilled.value
            const phoneValid = props.phone ? phoneValidator(props.phone) : phoneInputFilled.value
            const emailValid = props.email ? emailValidator(props.email) : emailInputFilled.value
            const commentsValid = commentsInputFilled.value
            const cprValid = !cprIsRequired.value || cprNumberInputFilled.value
            const cvrValid = !cvrIsRequired.value || cvrNumberInputFilled.value
            const addressValid = !addressIsRequired.value || addressFilled
            const paymentMethodValid = paymentMethodInputFilled.value

            const invalidFields = []
            if (!donationAmountValid) invalidFields.push('donationAmount')
            if (!companyNameValid) invalidFields.push('companyName')
            if (!firstNameValid) invalidFields.push('firstName')
            if (!lastNameValid) invalidFields.push('lastName')
            if (!phoneValid) invalidFields.push('phone')
            if (!emailValid) invalidFields.push('email')
            if (!commentsValid) invalidFields.push('comments')
            if (!cprValid) invalidFields.push('cpr')
            if (!cvrValid) invalidFields.push('cvr')
            if (!addressValid) invalidFields.push('address')
            if (!paymentMethodValid) invalidFields.push('paymentMethod')

            if (invalidFields.length > 0) {
                console.log('Invalid fields:', invalidFields)
                return false
            }
            return true
        }

        onMounted(() => {
            if (props.draft?.totalPrice) {
                const draftPreset = donationSettings.value.presets.find((val) => val == props.draft?.totalPrice)
                if (draftPreset) {
                    donationAmountInput.value = props.draft?.totalPrice
                } else {
                    if (typeof props.draft?.totalPrice === 'number') {
                        customDonationAmountInput.value = Number(props.draft.totalPrice).toLocaleString('da-DK', { maximumFractionDigits: 2 })
                    } else if (typeof props.draft?.totalPrice === 'string') {
                        customDonationAmountInput.value = props.draft.totalPrice
                    } else {
                        customDonationAmountInput.value = ''
                    }
                    customDonation.value = true
                }
            } else {
                donationAmountInput.value = props.draft?.totalPrice ?? donationSettings.value.presets[donationSettings.value.defaultPreset]
            }
        })

        const onSubmit = function () {
            unSubmitted.value = false
            if (!inputIsValid()) {
                scrollToFirstError('validation-error')
                return false
            }

            const donationPayload = {
                pageId: pageId.value,
                paymentMethod: paymentMethodInput.value,
                totalPrice: donationAmountInput.value,
                addressLine1: addressIsRequired.value ? addressLineInput.value : null,
                postalCode: addressIsRequired.value ? postalCodeInput.value : null,
                city: addressIsRequired.value ? cityInput.value : null,
                mobilePhone: props.phone ? props.phone : (showPersonalInformationInputValue ? phoneInput.value : null),
                email: props.email ? props.email : (showPersonalInformationInputValue ? emailInput.value : null),
                contactFirstName: props.firstName ? props.firstName : (showPersonalInformationInputValue ? firstNameInput.value : null),
                contactLastName: props.lastName ? props.lastName : (showPersonalInformationInputValue ? lastNameInput.value : null),
                cprNo: cprIsRequired.value ? cprNumberInput.value : null,
                cvrNo: cvrIsRequired.value ? cvrNumberInput.value : null,
                companyName: companyNameInput.value,
                comments: stripHtml(commentsInput.value),
                isTaxDeductible: taxDeductibleInput.value,
                isAnonymous: isAnonymous.value,
                customerType: customerTypeInput.value,
                referrer: document.referrer
            }

            store.dispatch('fundraising/submitPersonalDonation2', {
                fundraisingCampaignId: props.fundraisingCampaignId,
                payload: donationPayload
            })

            console.log('donationPayload', donationPayload)
        }

        const showCpr = computed(() => isPrivate.value && taxDeductibleInput.value)
        const showAddress = computed(() => isCompany.value || props.isMindegave)

        return {
            getLabel,
            donationSettings,
            companyNameInput,
            firstNameInput,
            lastNameInput,
            phoneInput,
            emailInput,
            addressLineInput,
            postalCodeInput,
            cityInput,
            cprNumberInput,
            cvrNumberInput,
            paymentMethodInput,
            anonymousDonationInput,
            isAnonymous,
            commentsInput,
            taxDeductibleInput,
            companyNameInputFilled,
            firstNameInputFilled,
            lastNameInputFilled,
            phoneInputFilled,
            emailInputFilled,
            addressLineInputFilled,
            postalCodeInputFilled,
            cityInputFilled,
            cprNumberInputFilled,
            cvrNumberInputFilled,
            paymentMethodInputFilled,
            donationAmountInput,
            donationAmountInputFilled,
            setDonationAmountFromSelector,
            commentsInputFilled,
            cancelUrl: props.cancelUrl,
            onSubmit,
            showCpr,
            isSubmitting,
            customerTypeInput,
            isPrivate,
            isCompany,
            showAddress,
            showPersonalInformationInputValue
        }
    }
}